import { useContext, useState } from "react";
import { UpAndDownProvider } from "./UpAndDown";
import { SnackbarContext } from "components/context/SnackbarProvider";

const handleWithoutError = (func, onError) => {
  if (!func) return async () => {};
  return async (...args) => {
    try {
      return await func(...args);
    } catch (error) {
      onError(error);
    }
  };
};

const buildCreateWrapper = (useCreate, Form) => {
  return ({ initialValues, onCreated, onCancel, ...props }) => {
    const { mutateAsync: mutateCreateAsync } = useCreate();
    const { displayMessage, displayError } = useContext(SnackbarContext);

    const handleCreate = (values, event) => {
      return mutateCreateAsync(values)
        .then((response) => {
          onCreated && onCreated(response);
          return response;
        })
        .catch(displayError);
    };

    return (
      <Form
        {...props}
        initialValues={initialValues}
        onSubmit={handleWithoutError(handleCreate, displayError)}
        onCancel={handleWithoutError(onCancel, displayError)}
        formType="create"
      />
    );
  };
};

const buildUpdateWrapper = (useGet, useUpdate, useDelete, Form) => {
  return ({ objectId, onUpdated, onDeleted, onCancel, ...props }) => {
    const { data, isSuccess, isError } = useGet(objectId);
    const { mutateAsync: mutateUpdateAsync } = useUpdate(objectId);
    const { mutateAsync: mutateDeleteAsync } = useDelete(objectId);
    const { displayMessage, displayError } = useContext(SnackbarContext);

    const handleUpdate = (values, event) => {
      return mutateUpdateAsync(values).then((response) => {
        onUpdated && onUpdated(response);
        return response;
      });
    };

    const handleDelete = (event) => {
      return mutateDeleteAsync().then((response) => {
        displayMessage("Deleted !", <></>, "success");
        onDeleted && onDeleted(response);
      });
    };

    if (isError) return <>Error in GenericModelForm</>;
    if (!isSuccess) return <></>;

    return (
      <>
        <Form
          key={objectId}
          {...props}
          onSubmit={handleWithoutError(handleUpdate, displayError)}
          onDelete={handleWithoutError(handleDelete, displayError)}
          onCancel={handleWithoutError(onCancel, displayError)}
          formType="update"
          initialValues={data}
          object={data}
        />
      </>
    );
  };
};

export const buildGenericModelForm = ({
  form,
  useGet,
  useCreate,
  useUpdate,
  useDelete,
}) => {
  const CreateForm = buildCreateWrapper(useCreate, form);
  const UpdateForm = buildUpdateWrapper(useGet, useUpdate, useDelete, form);

  return ({
    formType,
    values,
    objectId,
    setIsDirty: originalSetIsDirty,
    ...props
  }) => {
    const { displayMessage } = useContext(SnackbarContext);
    const [upAndDownObjectId, setUpAndDownObjectId] = useState(objectId);
    const [composedFormType, setComposedFormType] = useState(formType);
    const [isDirty, setIsDirty] = useState(false);

    const setIsDirtyComposed = (isDirty) => {
      setIsDirty(isDirty);
      originalSetIsDirty && originalSetIsDirty(isDirty);
    };

    if (composedFormType == "create")
      return (
        <CreateForm
          {...props}
          formType={composedFormType}
          setFormType={setComposedFormType}
          setObjectId={setUpAndDownObjectId}
          setIsDirty={setIsDirtyComposed}
          isDirty={isDirty}
        />
      );

    if (composedFormType == "update")
      return (
        <UpAndDownProvider
          values={values}
          onChange={(id) => {
            if (isDirty)
              return displayMessage("Did you forget to save your changes ?");
            return setUpAndDownObjectId(id);
          }}
        >
          <UpdateForm
            {...props}
            objectId={upAndDownObjectId}
            setIsDirty={setIsDirtyComposed}
            formType={composedFormType}
            isDirty={isDirty}
          />
        </UpAndDownProvider>
      );
    return (
      <>
        No form to display, did you forget to define the formType 'create' or
        'update' ?
      </>
    );
  };
};
